import GoogleIcon from '@mui/icons-material/Google';
import { Alert, Button, CircularProgress, Container, Dialog, Grid, Paper, Typography } from '@mui/material';
import { GoogleAuthenticationError, apiPost, appUrl, sessionSlice } from 'cdx-common';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '../../libraries/SessionProvider';
import { appStore } from '../../store';

const authenticationErrors: Record<GoogleAuthenticationError, string> = {
    configuration: 'La connexion Google n’est pas configurée sur le serveur.',
    device: 'Impossible de préparer cet appareil pour la connexion.',
    email: 'Google n’a pas fourni une adresse courriel vérifiée.',
    unauthorized: 'Cette adresse Google n’est attribuée à aucun usager de la plateforme.',
    user: 'L’usager associé à cette adresse est introuvable.'
};

const isAuthenticationError = (value: string | null): value is GoogleAuthenticationError => {
    return value !== null && value in authenticationErrors;
};

export const LoginView = () => {
    const [searchParams] = useSearchParams();
    const [requestingLogin, setRequestingLogin] = React.useState(false);
    const [requestingLoginFailed, setRequestingLoginFailed] = React.useState(false);
    const { user } = useSession();

    const requestLogin = React.useCallback(async () => {
        setRequestingLogin(true);
        setRequestingLoginFailed(false);

        const response = await apiPost('loginRequest', { expoDeviceInfo: {} });
        if (response === false) {
            setRequestingLoginFailed(true);
        } else {
            appStore.dispatch(sessionSlice.actions.authenticate({
                userId: user?.id ?? null,
                sessionId: response.deviceId
            }));
        }
        setRequestingLogin(false);
    }, [user?.id]);

    React.useEffect(() => {
        void requestLogin();
    }, [requestLogin]);

    const authError = searchParams.get('authError');

    if (requestingLogin) {
        return <Dialog open={true}>
            <Typography variant='body1' sx={{ padding: 2 }}>
                <CircularProgress size={20} sx={{ marginRight: 1 }} />
                Autorisation auprès du serveur...
            </Typography>
        </Dialog>;
    }

    return <Container>
        <Grid container justifyContent='center' spacing={2}>
            <Grid item xs={12} sm={10} md={8} lg={6} alignSelf='center'>
                <Paper sx={{ padding: 3, textAlign: 'center' }}>
                    <Typography variant='h5' sx={{ marginBottom: 2 }}>Connexion à Construction CDX</Typography>

                    {isAuthenticationError(authError) &&
                        <Alert severity='error' sx={{ marginBottom: 2 }}>{authenticationErrors[authError]}</Alert>}

                    {requestingLoginFailed
                        ? <Alert severity='info' action={<Button onClick={() => void requestLogin()}>Réessayer</Button>}>
                            Impossible de préparer cet appareil.
                        </Alert>
                        : <Button
                            component='a'
                            href={appUrl(user ? 'googleLoginOtherAccount' : 'googleLogin')}
                            startIcon={<GoogleIcon />}
                            variant='contained'
                        >
                            {user ? 'Utiliser un autre compte Google' : 'Continuer avec Google'}
                        </Button>}
                </Paper>
            </Grid>
        </Grid>
    </Container>;
};
